import React, { useEffect, useState } from 'react';
import { Loader, Logo, ProfilePicture } from '@getro/rombo';
import { Box, Flex, Link, Text } from 'rebass/styled-components';
import { Switch, Route, useParams, useHistory } from 'react-router-dom';

import { ContactDetail } from 'components/organisms/contactDetail';
import { ExtendedContact } from 'pages/contactsExtended/extendedContact';
import { useLocalStorage } from 'hooks/useLocalStorage';
import pluralize from 'pluralize';
import { trackEvent } from '../../helpers/analytics';
import useContactsFilterCount from '../../hooks/useContactsFilterCount';
import { MainSkeleton, TitleSkeleton } from './skeletons';
import useSharedLists from './useSharedLists';
import AuthModal from './authModal';
import NoList from './noList';
import FixedHeader from './fixedHeader/fixedHeader';
import { RequestBulkIntro } from './requestBulkIntro';
import { NotesDrawer } from './components/notesDrawer';
import { useNotesDrawerState } from './components/notesDrawer/hooks/useNotesDrawerState';

const SharedLists = () => {
  const {
    slug,
    isInitialized,
    isFetching,
    isLoading: isLoadingSharedList,
    contacts,
    listInfo,
    meta,
    queryParams,
    setQueryParams,
    showAuthentication,
    onSubmitPassword,
    nextPage,
    status,
    activeNetwork,
    customFields,
  } = useSharedLists();
  const history = useHistory();
  const params = useParams();
  const [requestedContacts] = useLocalStorage(`requested_intros_${params.slug}`, [], { reactive: true });
  const filtersCount = useContactsFilterCount(queryParams?.filters);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const {
    isOpen: isNotesDrawerOpen,
    contactId: notesDrawerContactId,
    openDrawer: openNotesDrawer,
    closeDrawer: closeNotesDrawer,
  } = useNotesDrawerState();

  useEffect(() => {
    if (isInitialized) {
      const viewMapping = {
        table_view: 'table',
        cards_view: 'card',
      };
      const selectedView = viewMapping[listInfo?.shareableLayout] || 'table';

      trackEvent('contacts:shared_list:view_shared_list', {
        list_slug: slug,
        collection_id: listInfo.collection.id,
        list_type: 'static',
        object_type: 'people',
        intro_requests_enabled: listInfo.shareableView?.introRequestsEnabled,
        view_mode: selectedView,
      });
    }
  }, [isInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  const showNetworkLogo =
    ((!isInitialized && !isFetching && status === 'rejected') || showAuthentication) && activeNetwork;
  const isLoading = !isInitialized && status !== 'rejected' && !showAuthentication;

  return (
    <Box
      display="grid"
      gridTemplateColumns="1fr 1fr"
      sx={{ height: ['auto', 'auto', '100vh'], overflow: ['visible', 'visible', 'hidden'] }}
    >
      <Box px="16px">
        {isInitialized && <FixedHeader listInfo={listInfo} />}
        {!isInitialized && (
          <Flex
            justifyContent={showNetworkLogo || isLoading ? 'space-between' : 'flex-end'}
            alignItems={status === 'rejected' ? 'center' : 'flex-start'}
            flexDirection={['column-reverse', 'row']}
            sx={{ gap: '32px' }}
          >
            {!isInitialized && status !== 'rejected' && !showAuthentication && <TitleSkeleton />}
            {showNetworkLogo && (
              <Flex height="48px" alignItems="center">
                <ProfilePicture variant="square" name={activeNetwork.name} imageUrl={activeNetwork.logoUrl} />
                <Text fontSize="14px" fontWeight="500" color="text.main">
                  {activeNetwork.name}
                </Text>
              </Flex>
            )}
            <Flex
              sx={{
                columnGap: '16px',
                width: ['100%', 'unset', 'unset'],
                flexDirection: ['row-reverse', 'row', 'row'],
              }}
              alignItems="baseline"
              alignContent="baseline"
            >
              <Link
                href="https://www.getro.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ flexGrow: [1, 'unset', 'unset'] }}
              >
                <Logo color="primary" width="96px" />
              </Link>
            </Flex>
          </Flex>
        )}
        {!showAuthentication && !(isInitialized && !(isLoadingSharedList && queryParams.page === 1)) && (
          <Flex mt="40px" flexDirection={['column', 'column', 'row']}>
            {!isInitialized && status !== 'rejected' && (
              <Flex flex="1" height="70vh" alignItems="center" justifyContent="center">
                <Loader />
              </Flex>
            )}
            {!isInitialized && status === 'rejected' && (
              <Flex flex="1" height="70vh" alignItems="center" justifyContent="center">
                <NoList networkName={activeNetwork?.name} />
              </Flex>
            )}

            {isInitialized && isFetching && queryParams.page === 1 && (
              <Flex flexGrow="1" maxWidth="900px">
                <MainSkeleton />
              </Flex>
            )}
          </Flex>
        )}

        {!isInitialized && showAuthentication && (
          <AuthModal networkName={activeNetwork?.name} onEnterPassword={onSubmitPassword} />
        )}
      </Box>
      {!showAuthentication && isInitialized && !(isLoadingSharedList && queryParams.page === 1) && (
        <Box pt="16px">
          <ExtendedContact
            listAction={
              <Flex alignItems="stretch" justifyContent="space-between">
                <Text sx={{ fontSize: '12px' }}>{`Showing ${meta.queryTotal} ${pluralize(
                  'contact',
                  meta.queryTotal,
                )}`}</Text>
              </Flex>
            }
            otherToolbarOptions={
              <RequestBulkIntro
                selectedContacts={selectedContacts}
                listInfo={listInfo}
                params={params}
                setSelectedContacts={setSelectedContacts}
                contacts={contacts}
              />
            }
            onSetFilters={(newFilters) => {
              setSelectedContacts(new Set());
              setQueryParams({ ...queryParams, filters: newFilters, page: 1 });
            }}
            onChangeSelectedContact={(_, contact) => {
              setSelectedContacts((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(contact.id)) {
                  newSet.delete(contact.id);
                } else {
                  newSet.add(contact.id);
                }
                return newSet;
              });
            }}
            meta={meta}
            queryParams={queryParams}
            selectedRows={Array.from(selectedContacts)}
            setSelectedRows={(newSelectedRows) => setSelectedContacts(new Set(newSelectedRows))}
            onLoadMore={nextPage}
            data={contacts}
            disabledContacts={requestedContacts.map((requestedContact) => requestedContact.id)}
            isLoading={isLoading}
            isFetching={isFetching}
            filtersCount={filtersCount}
            activeNetwork={activeNetwork}
            showAllContactsEmptyState={false}
            listId={listInfo.id}
            list={listInfo}
            emptyStateUrl={`/networks/${activeNetwork?.slug}/contacts`}
            showFiltersBottomBar={false}
            isLoadingAI={false}
            filtersTitle="Filters"
            type="public"
            page="shared_list"
            showListFilters={false}
            cardSx={{
              height: ['100%', '100%', 'calc(100vh -204px)'],
            }}
            tableSx={{
              height: 'calc(100vh - 204px)',
              '.ag-column-first': {
                paddingLeft: listInfo.shareableView?.introRequestsEnabled ? '0px' : '8px',
              },
              '.ag-pinned-left-header': {
                '.ag-header-cell-checkbox ': {
                  display: 'none',
                },

                '.ag-header-cell': {
                  left: listInfo.shareableView?.introRequestsEnabled ? '10px!important' : 'initial',
                  width: listInfo.shareableView?.introRequestsEnabled ? 'calc(100% - 10px)!important' : 'initial',
                },
              },
            }}
            onOpenNotesDrawer={openNotesDrawer}
            customFields={customFields}
          />
        </Box>
      )}
      {listInfo?.shareableLayout === 'cards_view' && (
        <Switch>
          <Route path="/shared-lists/:slug/:contactId">
            <ContactDetail
              onClose={() => {
                history.push(`/shared-lists/${slug}${history.location.search}`);
              }}
              type="public"
              showMoreOptions={false}
              queryParams={queryParams}
              page="shared_list"
            />
          </Route>
        </Switch>
      )}
      {listInfo?.shareableLayout === 'table_view' && (
        <NotesDrawer
          isOpen={isNotesDrawerOpen}
          contactId={notesDrawerContactId}
          listInfo={listInfo}
          onClose={closeNotesDrawer}
        />
      )}
    </Box>
  );
};

export default SharedLists;
